import React, { useState, useContext, memo, useCallback } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Button, Input, CircularProgress, DialogActions,
	 FormHelperText, InputLabel, FormControl,
	 DialogContent, DialogTitle, Dialog, DialogContentText } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import HeaderBar from './HeaderBar.js';
import Typography from '@material-ui/core/Typography';
import firebase from '../Firebase.js';
import { AuthContext } from '../contexts/AuthContext.js';
import { isEmailValid, isPasswordValid, BottomMessage } from '../Common.js';

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      container: {
	overflow: 'auto',
	marginTop: theme.spacing(4),
	[theme.breakpoints.up('sm')] : {
	  height: `calc(100% - 64px)`
	},
	[theme.breakpoints.down('xs')] : {
	  height: `calc(100% - 56px)`
	},
      },
      inputBox: {
	paddingLeft: theme.spacing(1),
	paddingRight: theme.spacing(1),
      },
      link: {
	color: theme.palette.primary.yellow,
	fontSize: 23,
      },
      loginBtn: {
	borderRadius: 25,
	background: theme.palette.primary.yellow,
	color: theme.palette.primary.brown,
	marginTop:  theme.spacing(1),
	marginBottom:  theme.spacing(2),
	'&:hover': {
	  background: theme.palette.primary.yellow,
	},
      },
      loading: {
	marginTop:  theme.spacing(1),
	marginBottom:  theme.spacing(2),
      }
    }),
);

const signOut = () => {
  firebase.auth().signOut();
};

const ForgotPasswordDialog = memo(({ isOpen, close }) => {
  const { t } = useTranslation();
  const [ email, setEmail ] = useState('');
  const [ sending, setSending ] = useState(false);

  const cleanAndClose = () => {
    if (sending) {
      return;
    }
    setEmail('');
    close();
  };

  const sendResetPasswordEmail = async () => {
    setSending(true);
    try {
      await firebase.auth().sendPasswordResetEmail(email);
    }
    catch (error) {
      console.log('reset email error ' , error);
    }
    finally {
      setSending(false);
      cleanAndClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={cleanAndClose}>
      <DialogTitle style={{ cursor: 'move' }}>
	{t('forgot-your-password')}
      </DialogTitle>
      <DialogContent>
	<DialogContentText>
	  {t('you-will-get-a-reset-email')}
	</DialogContentText>
	<Input
	  value={email}
	  placeholder={t('email')}
	  onChange={(e) => setEmail(e.target.value)}
	  type='email' />
      </DialogContent>
      <DialogActions>
	{
	  sending ? (<CircularProgress size={15} color='secondary' />) :
	    (<Button onClick={() => sendResetPasswordEmail()}
		     disabled={!isEmailValid(email)}
		     color='secondary'>
	       {t('send')}
	     </Button>)
	}
      </DialogActions>
    </Dialog>
  );
});

const VerificationDialog = memo(() => {
  const { t } = useTranslation();
  const { authUser } = useContext(AuthContext);
  const classes = useStyles();
  const sendEmailVerificationAndClose = () => {
    firebase.auth().currentUser.sendEmailVerification();
    signOut();
  };
  return (
    <Dialog
      open={!!authUser && !authUser.emailVerified}>
      <DialogTitle style={{ cursor: 'move' }} >
	{t('verify-your-email')}
      </DialogTitle>
      <DialogContent>
	<DialogContentText>
	  {t('you-must-verify-its-your-email')}
	</DialogContentText>
	<Button onClick={sendEmailVerificationAndClose}
		variant='contained'
		className={classes.loginBtn}
		color='secondary'>
	  {t('send-verification-email')}
	</Button>
      </DialogContent>
      <DialogActions>
	<Button onClick={signOut} color='secondary'>
	  {t('not-now')}
	</Button>
      </DialogActions>
    </Dialog>
  );
});

const MissingAdminAcceptDialog = () => {
  const { t } = useTranslation();
  const { authUser, isAuthTrainerOrAdmin } = useContext(AuthContext);
  return (
    <Dialog
      open={!!authUser && authUser.emailVerified && !isAuthTrainerOrAdmin()}>
      <DialogTitle style={{ cursor: 'move' }} >
	{t('sorry')}
      </DialogTitle>
      <DialogContent>
	<DialogContentText>
	  {t('dogo-admin-still-has-to-accept-you-as-a-trainer')}
	</DialogContentText>
	<DialogContentText>
	  {t('write-them-an-email-to') + ' info@dogo.app'}
	</DialogContentText>
      </DialogContent>
      <DialogActions>
	<Button onClick={signOut} color='secondary'>
	  {t('ok')}
	</Button>
      </DialogActions>
    </Dialog>
  );
};


const RegisterBtn = withRouter(
  ({ history, disabled }) => {
    const { t } = useTranslation();
    return (
      <Button onClick={() => history.push('/register') }
	      disabled={disabled}
	      color='secondary'>
	{t('register-as-trainer')}
      </Button>
    );
  }
);


export default () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [signingIn, setSigningIn] = useState(false);
  const { isAuthTrainerOrAdmin, authChanged } = useContext(AuthContext);
  const [loginInput, setLoginInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [forgotPassword, setForgotPassword] = useState(false);

  const login = async () => {
    setSigningIn(true);
    try {
      await firebase.auth().signInWithEmailAndPassword(loginInput, passwordInput);
      setSigningIn(false);
    }
    catch(error) {
      setSigningIn(false);
      switch (error.code) {
      case 'auth/user-not-found':
	setErrorMessage(t(error.code));
	break;
      case 'auth/wrong-password':
	setErrorMessage(t(error.code));
	break;
      default:
	setErrorMessage(t('general-error'));
      }
    }
  };

  if (isAuthTrainerOrAdmin()) {
    return <Redirect to={{ pathname: '/exams' }} />;
  }
  else {
    return (
      <div style={{height: '100%'}}>
	<HeaderBar LComp={<div/>}
		   MComp={<Typography variant='h6' align='center'>{t('login')}</Typography>}
		   RComp={<div/>}/>
	<Grid container
	      wrap='nowrap'
	      direction='column'
	      alignItems='center'
	      className={classes.container}>
	  <Grid item container direction='column' className={classes.inputBox}>
	    <FormControl color='secondary'>
	      <InputLabel> {t('email')} </InputLabel>
	      <Input
		value={loginInput}
		onChange={e => setLoginInput(e.target.value)} />
	      <FormHelperText>
		{loginInput && !isEmailValid(loginInput) ? t('email-not-valid') : ''}
	      </FormHelperText>
	    </FormControl>
	  </Grid>
	  <Grid item container direction='column' className={classes.inputBox}>
	    <FormControl color='secondary'>
	      <InputLabel> {t('password')} </InputLabel>
	      <Input
		value={passwordInput}
		onChange={(e) => setPasswordInput(e.target.value)}
		type='password' />
	      <FormHelperText>
		{ passwordInput && !isPasswordValid(passwordInput) ? t('must-be-at-least-6-chars') : ''}
	      </FormHelperText>
	    </FormControl>
	  </Grid>
	  <Grid item>
	    { signingIn || authChanged ?
	      (<CircularProgress color='secondary' className={classes.loading} />)
	      :
	      <Button label='sign-out'
		      size='large'
		      disabled={!isEmailValid(loginInput) || !isPasswordValid(passwordInput)}
		      onClick={() => login()}
		      variant='contained'
		      className={classes.loginBtn}>
		{ t('login') }
	      </Button>
	    }
	  </Grid>
	  <Grid item>
	    <Button onClick={() => setForgotPassword(true)}
		    disabled={signingIn || authChanged}
		    color='secondary'>
	      { t('forgot-password')}
	    </Button>
	  </Grid>
	  <Grid item>
	    <RegisterBtn disabled={signingIn || authChanged}/>
	  </Grid>
	  <VerificationDialog/>
	  <MissingAdminAcceptDialog/>
	  <ForgotPasswordDialog isOpen={forgotPassword}
				close={useCallback(() => setForgotPassword(false), [])}/>
	  <BottomMessage open={!!errorMessage}
			 message={errorMessage}
			 close={() => setErrorMessage(null)}/>
	</Grid>
      </div>
    );
  }
};
